import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setSubmitMessage('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://readdy.ai/api/form/d5i68fjihlvellpk58rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <footer className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Company Info */}
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-10 h-10 bg-teal-500 rounded-lg flex items-center justify-center">
                <i className="ri-trophy-line text-xl text-white"></i>
              </div>
              <span className="text-xl font-bold">TeamPro</span>
            </div>
            <p className="text-gray-400 text-sm leading-relaxed">
              Revolutionary sports team management platform. Automate operations, track performance, and focus on winning.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#features" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  Features
                </a>
              </li>
              <li>
                <a href="#equipment-store" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  Equipment Store
                </a>
              </li>
              <li>
                <Link to="/orders" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  Orders Dashboard
                </Link>
              </li>
              <li>
                <Link to="/press" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  Press Kit
                </Link>
              </li>
              <li>
                <Link to="/deploy-guide" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  Deploy Guide
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="font-bold mb-4">Resources</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="#about" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  About Us
                </a>
              </li>
              <li>
                <a href="#testimonials" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  Testimonials
                </a>
              </li>
              <li>
                <Link to="/press" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  Media Kit
                </Link>
              </li>
              <li>
                <a href="#contact" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                  Contact
                </a>
              </li>
            </ul>
          </div>

          {/* Newsletter */}
          <div>
            <h4 className="font-bold mb-4">Stay Updated</h4>
            <p className="text-gray-400 text-sm mb-4">
              Get the latest updates on features and sports management tips.
            </p>
            <form onSubmit={handleSubmit} className="space-y-3" data-readdy-form id="newsletter-footer">
              <input
                type="email"
                name="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full px-4 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:outline-none focus:border-teal-500 text-sm"
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-teal-500 text-white py-2 rounded-lg font-semibold hover:bg-teal-600 transition-colors text-sm whitespace-nowrap cursor-pointer disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            {submitMessage && (
              <p className={`text-sm mt-2 ${submitMessage.includes('Success') ? 'text-teal-400' : 'text-red-400'}`}>
                {submitMessage}
              </p>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-700 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-400 text-sm">
              © 2025 TeamPro Management. All rights reserved. | <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:text-teal-300 transition-colors cursor-pointer">Powered by Readdy</a>
            </p>
            <div className="flex items-center space-x-6">
              <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                <i className="ri-twitter-x-line text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                <i className="ri-linkedin-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                <i className="ri-facebook-fill text-xl"></i>
              </a>
              <a href="#" className="text-gray-400 hover:text-teal-400 transition-colors cursor-pointer">
                <i className="ri-instagram-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
